import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { collection, query, onSnapshot, addDoc, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Discussion } from '../../types';
import { MessageSquare, Send, User } from 'lucide-react';

interface FirestoreDiscussion extends Omit<Discussion, 'createdAt'> {
  createdAt: Timestamp | Date;
}

const TeamDiscussion: React.FC = () => {
  const { userProfile } = useAuth();
  const [discussions, setDiscussions] = useState<FirestoreDiscussion[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query(collection(db, 'discussions'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const discussionsData: FirestoreDiscussion[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        discussionsData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        } as FirestoreDiscussion);
      });
      setDiscussions(discussionsData);
    }, (error) => {
      console.error('Error fetching discussions:', error);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [discussions]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile || !newMessage.trim()) return;

    setLoading(true);
    try {
      const newDiscussion: Omit<Discussion, 'id'> = {
        message: newMessage.trim(),
        authorId: userProfile.uid,
        authorName: userProfile.displayName || userProfile.email?.split('@')[0] || 'Anonymous',
        authorProfilePicture: userProfile.profilePicture,
        createdAt: serverTimestamp(),
        messageType: 'text',
        isRead: { [userProfile.uid]: true }
      };

      await addDoc(collection(db, 'discussions'), newDiscussion);
      setNewMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: Date | { toDate: () => Date }) => {
    const dateObj = date && 'toDate' in date ? date.toDate() : new Date(date);
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isConsecutiveMessage = (currentMsg: FirestoreDiscussion, prevMsg: FirestoreDiscussion | undefined) => {
    if (!prevMsg) return false;
    
    const currentTime = currentMsg.createdAt instanceof Date 
      ? currentMsg.createdAt.getTime() 
      : currentMsg.createdAt.toDate().getTime();
      
    const prevTime = prevMsg.createdAt instanceof Date 
      ? prevMsg.createdAt.getTime() 
      : prevMsg.createdAt.toDate().getTime();
    
    const timeDiff = currentTime - prevTime;
    const fiveMinutes = 5 * 60 * 1000;
    
    return currentMsg.authorId === prevMsg.authorId && timeDiff < fiveMinutes;
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center">
            <MessageSquare className="mr-3 text-primary-600" size={24} />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Team Discussion</h1>
              <p className="text-sm text-gray-600">Real-time chat with your team members</p>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            {discussions.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Start the conversation by sending the first message.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {discussions.map((discussion, index) => {
                  const prevMessage = index > 0 ? discussions[index - 1] : undefined;
                  const isConsecutive = isConsecutiveMessage(discussion, prevMessage);
                  const isOwnMessage = discussion.authorId === userProfile?.uid;

                  return (
                    <div key={discussion.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                        {!isConsecutive && (
                          <div className={`flex items-center mb-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex items-center ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                              <div className="bg-gray-300 rounded-full h-6 w-6 flex items-center justify-center overflow-hidden">
                                {discussion.authorProfilePicture ? (
                                  <img 
                                    src={discussion.authorProfilePicture} 
                                    alt={discussion.authorName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <User className="h-4 w-4 text-gray-600" />
                                )}
                              </div>
                              <span className={`text-xs text-gray-600 ${isOwnMessage ? 'mr-2' : 'ml-2'}`}>
                                {discussion.authorName}
                              </span>
                            </div>
                          </div>
                        )}
                        <div
                          className={`px-4 py-2 rounded-lg ${
                            isOwnMessage
                              ? 'bg-primary-600 text-white'
                              : 'bg-white text-gray-900 shadow-sm'
                          } ${isConsecutive ? 'mt-1' : 'mt-2'}`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{discussion.message}</p>
                          <p className={`text-xs mt-1 ${isOwnMessage ? 'text-primary-200' : 'text-gray-500'}`}>
                            {formatTime(discussion.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Message Input */}
        <div className="bg-white border-t px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSendMessage} className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={1}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !newMessage.trim()}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Send size={20} />
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-2">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TeamDiscussion;